import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiPlus, FiArrowLeft, FiLoader, FiCheck,
  FiRefreshCw, FiImage, FiX,
} from 'react-icons/fi';
import { apiRequest, apiUpload, getUserInfo, getAccessToken, decodeToken } from '../utils/auth';
import {
  GENERATION_START_TIMEOUT_MS,
  GenerationStartResponse,
  pollGenerationUntilDone,
} from '../utils/generationPolling';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import './CreateExtensionModal.css';
import './ExtensionDetailsModal.css';

interface ImageModelOption {
  _id: string;
  /** Identity — stored on Extension.aiModel */
  model_id: string;
  /** Model id sent to the provider; several entries may share one */
  model_name: string;
  display_name?: string;
  credit: string;
  aspect_ratio?: string[];
  enabled?: boolean;
}

interface Category {
  _id: string;
  category_id: string;
  label: { en: string; 'zh-hk'?: string };
  order: number;
}

interface SamplePair {
  key: string;
  inputImages: File[];
  outputImageBase64: string; // base64 data URL for display
  originalImageKeys?: string[];
  enhancedImageKey?: string;
}

// One entry in the combined input row. Every added photo starts as a user input; toggling
// `isReference` promotes it to a creator reference (prepended to the generation).
interface RowImage {
  id: string;
  file: File;
  preview: string;      // base64 data URL for display
  isReference: boolean;
}

interface CreateExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const ALL_ASPECT_RATIOS = ['original', '1:1', '2:3', '3:2', '3:4', '4:3', '16:9', '9:16'] as const;

export default function CreateExtensionModal({ isOpen, onClose, onCreated }: CreateExtensionModalProps) {
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 state
  const [prompt, setPrompt] = useState('');
  // Single combined input row (mirrors the mobile create flow). Every added photo starts as a
  // user input ("Image N"); clicking it toggles it to a creator reference ("Reference N") that is
  // prepended to the generation instead of counting as a sample input. References & inputs are
  // mutually exclusive, tracked by the `isReference` flag on each ordered entry.
  const [rowImages, setRowImages] = useState<RowImage[]>([]);
  // Reference S3 keys captured from the latest generation response — persisted on the extension.
  const [referenceImageKeys, setReferenceImageKeys] = useState<string[]>([]);
  const [aiModel, setAiModel] = useState('');
  const [aspectRatio, setAspectRatio] = useState('3:4');
  const [imageModels, setImageModels] = useState<ImageModelOption[]>([]);
  const [isDynamicInput, setIsDynamicInput] = useState(false);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState<string | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [lastGeneratedKeys, setLastGeneratedKeys] = useState<{
    originalImageKeys: string[];
    enhancedImageKey: string;
  } | null>(null);

  // Step 2 state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageRequirements, setImageRequirements] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [samplePairs, setSamplePairs] = useState<SamplePair[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // For adding more samples in step 2
  const [step2InputImages, setStep2InputImages] = useState<File[]>([]);
  const [step2Previews, setStep2Previews] = useState<string[]>([]);
  const [showStep2GenerateModal, setShowStep2GenerateModal] = useState(false);
  const [isStep2Generating, setIsStep2Generating] = useState(false);
  const [step2GeneratedPreview, setStep2GeneratedPreview] = useState<string | null>(null);
  const [step2GenerationError, setStep2GenerationError] = useState<string | null>(null);
  const [step2GeneratedKeys, setStep2GeneratedKeys] = useState<{
    originalImageKeys: string[];
    enhancedImageKey: string;
  } | null>(null);

  // Category drag state
  const [draggedCatIdx, setDraggedCatIdx] = useState<number | null>(null);

  // Input-row drag-to-reorder state
  const [draggedImageIdx, setDraggedImageIdx] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    loadImageModels();
    loadCategories();
  }, [isOpen]);

  const loadImageModels = async () => {
    try {
      const res = await apiRequest<{ success: boolean; data: ImageModelOption[] }>('/api/admin/image-models');
      if (res.success && res.data) {
        const enabledModels = res.data.filter(m => m.enabled !== false);
        setImageModels(enabledModels);
      }
    } catch (err) { console.error('Failed to load models', err); }
  };

  const loadCategories = async () => {
    try {
      const res = await apiRequest<{ success: boolean; data: Category[] }>('/api/admin/categories?limit=200');
      if (res.success && res.data) setAllCategories(res.data);
    } catch (err) { console.error('Failed to load categories', err); }
  };

  const selectedModelObj = imageModels.find(m => m.model_id === aiModel);
  const selectedModelCredit = selectedModelObj?.credit || '?';

  // Non-reference photos count as the sample inputs; references are prepended separately.
  const inputImageCount = rowImages.filter(r => !r.isReference).length;

  // Critical fields required before publishing from Step 2 (mirrors the mobile create flow's
  // CreateExtensionStep2View). Collected as a list — not just a boolean — so a click on the
  // dimmed Publish button can surface exactly what's still missing. Draft-saving stays lenient.
  const missingRequirements: string[] = [];
  if (!selectedModelObj) missingRequirements.push('Active AI model');
  if (!name.trim()) missingRequirements.push('Extension name');
  if (selectedCategories.length === 0) missingRequirements.push('Categories');
  if (!description.trim()) missingRequirements.push('Description');
  const hasSample = samplePairs.length > 0;
  const isStep2Valid = hasSample && missingRequirements.length === 0;

  // Whether the admin has entered anything worth warning about before discarding on close.
  // A model selection also counts as content to preserve on close.
  const hasUnsavedContent =
    aiModel !== '' ||
    prompt.trim() !== '' ||
    rowImages.length > 0 ||
    samplePairs.length > 0 ||
    name.trim() !== '' ||
    description.trim() !== '' ||
    imageRequirements.trim() !== '' ||
    keywords.length > 0 ||
    selectedCategories.length > 0;

  // Aspect ratio options derived from the selected model's aspect_ratio array.
  // Falls back to all known ratios when the model doesn't specify any.
  const aspectRatioOptions = useMemo(() => {
    const selectedModelObj = imageModels.find(m => m.model_id === aiModel);
    const ratios = selectedModelObj?.aspect_ratio && selectedModelObj.aspect_ratio.length > 0
      ? selectedModelObj.aspect_ratio
      : [...ALL_ASPECT_RATIOS];
    return ratios.map(id => (id === 'original' ? 'original' : id.replace('-', ':')));
  }, [imageModels, aiModel]);

  // Reset aspect ratio when the selected model doesn't support the current choice.
  useEffect(() => {
    if (aspectRatioOptions.length > 0 && !aspectRatioOptions.includes(aspectRatio)) {
      setAspectRatio(aspectRatioOptions[0]);
    }
  }, [aspectRatioOptions, aspectRatio]);

  const resetAll = () => {
    setStep(1);
    setPrompt(''); setRowImages([]); setReferenceImageKeys([]); setAiModel('');
    setAspectRatio('3:4'); setIsGenerating(false); setGeneratedPreview(null);
    setGenerationError(null); setShowGenerateModal(false); setLastGeneratedKeys(null);
    setIsDynamicInput(false);
    setName(''); setDescription(''); setImageRequirements('');
    setKeywords([]); setKeywordInput(''); setSelectedCategories([]);
    setSamplePairs([]); setIsSaving(false); setSaveError(null);
    setStep2InputImages([]); setStep2Previews([]); setShowStep2GenerateModal(false);
    setIsStep2Generating(false); setStep2GeneratedPreview(null);
    setStep2GenerationError(null); setStep2GeneratedKeys(null);
  };

  // Reset + close without prompting — used after a successful publish/save (nothing to lose).
  const doClose = () => { resetAll(); onClose(); };

  // User-initiated close (× button, Cancel, or backdrop click) discards everything entered so
  // far, so confirm first whenever there's unsaved content.
  const handleClose = () => {
    if (hasUnsavedContent && !window.confirm('Discard this lens? Your unsaved changes will be lost.')) {
      return;
    }
    doClose();
  };

  // Escape mirrors the buttons each layer actually offers: it discards the form
  // (with the same confirm as the × button), and on a generation modal it only
  // cancels while Cancel is on screen — never mid-generation, and never over an
  // unaccepted preview, since that image has already cost credits. The guarded
  // no-op still swallows the key so it can't close the form underneath.
  useEscapeToClose(isOpen, handleClose);
  useEscapeToClose(showGenerateModal, () => {
    if (!isGenerating && !generatedPreview) setShowGenerateModal(false);
  });
  useEscapeToClose(showStep2GenerateModal, () => {
    if (isStep2Generating || step2GeneratedPreview) return;
    setShowStep2GenerateModal(false);
    setStep2InputImages([]);
    setStep2Previews([]);
  });

  // === COMBINED INPUT ROW HANDLING ===
  // New photos are appended as user inputs; the creator taps one to mark it a reference.
  const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    files.forEach(file => {
      const id = `img-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const reader = new FileReader();
      reader.onloadend = () => {
        setRowImages(prev => [...prev, { id, file, preview: reader.result as string, isReference: false }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  // Toggle an image between user-input and creator-reference roles (mutually exclusive).
  const toggleReference = (id: string) => {
    setRowImages(prev => prev.map(r => (r.id === id ? { ...r, isReference: !r.isReference } : r)));
  };

  const removeRowImage = (id: string) => {
    setRowImages(prev => prev.filter(r => r.id !== id));
  };

  // Drag-to-reorder the input row (labels renumber per-role to follow the new order).
  const handleImageDragStart = (idx: number) => setDraggedImageIdx(idx);
  const handleImageDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedImageIdx === null || draggedImageIdx === idx) return;
    setRowImages(prev => {
      const next = [...prev];
      const [dragged] = next.splice(draggedImageIdx, 1);
      next.splice(idx, 0, dragged);
      return next;
    });
    setDraggedImageIdx(idx);
  };
  const handleImageDragEnd = () => setDraggedImageIdx(null);

  // === STEP 1 GENERATE ===
  const handleGenerate = async () => {
    if (!selectedModelObj) { setGenerationError('Choose an active model before generating.'); return; }
    const references = rowImages.filter(r => r.isReference);
    const inputs = rowImages.filter(r => !r.isReference);
    if (!prompt.trim()) { setGenerationError('Prompt is required'); return; }
    if (inputs.length === 0) { setGenerationError('At least one input image is required'); return; }

    setShowGenerateModal(true);
    setIsGenerating(true);
    setGeneratedPreview(null);
    setGenerationError(null);

    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('supportsAsync', 'true'); // opt into the async 202 + polling backend path
      if (aiModel) formData.append('model', aiModel);
      if (aspectRatio) formData.append('aspectRatio', aspectRatio);
      // References are prepended to the generation (sent as fresh files); inputs are the samples.
      references.forEach(r => formData.append('referenceImage', r.file));
      inputs.forEach(r => formData.append('images', r.file));

      // Kick off the async generation — returns a generationId immediately (202).
      const startRes = await apiUpload<GenerationStartResponse>(
        '/api/gemini/transform-with-extension',
        formData,
        GENERATION_START_TIMEOUT_MS,
      );

      if (!startRes.success || !startRes.generationId) {
        throw new Error('Failed to start image generation');
      }

      // Poll until the background generation completes (throws on failure/timeout).
      const generation = await pollGenerationUntilDone(startRes.generationId);

      const imageUrl = generation.enhancedImage?.url;
      if (!imageUrl) throw new Error('No image returned from generation');
      setGeneratedPreview(imageUrl);

      // Prefer the keys from the completed record; fall back to the ones returned at start.
      const originalImageKeys = generation.originalImages?.map(img => img.key) ?? startRes.originalImageKeys;
      const enhancedImageKey = generation.enhancedImage?.key;
      if (originalImageKeys && enhancedImageKey) {
        setLastGeneratedKeys({ originalImageKeys, enhancedImageKey });
      }
      setReferenceImageKeys(startRes.referenceImageKeys || []);
      // Also generate description in parallel
      if (!description) {
        apiRequest<{ success: boolean; description?: string }>('/api/gemini/generate-description', {
          method: 'POST',
          body: JSON.stringify({ prompt: prompt.trim() }),
        }).then(descRes => {
          if (descRes.success && descRes.description) setDescription(descRes.description);
        }).catch(() => {});
      }
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptGeneration = () => {
    if (!generatedPreview) return;
    const newPair: SamplePair = {
      key: `sample-${Date.now()}`,
      inputImages: rowImages.filter(r => !r.isReference).map(r => r.file),
      outputImageBase64: generatedPreview,
      ...(lastGeneratedKeys && {
        originalImageKeys: lastGeneratedKeys.originalImageKeys,
        enhancedImageKey: lastGeneratedKeys.enhancedImageKey,
      }),
    };
    setSamplePairs([newPair]);
    setShowGenerateModal(false);
    setStep(2);
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    handleGenerate();
  };

  // === STEP 2: ADD MORE SAMPLES ===
  const handleStep2OpenModal = () => {
    setStep2InputImages([]);
    setStep2Previews([]);
    setShowStep2GenerateModal(true);
    setStep2GeneratedPreview(null);
    setStep2GenerationError(null);
    setIsStep2Generating(false);
  };

  const handleStep2SlotSelect = (slotIdx: number) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = (ev) => {
      const file = (ev.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setStep2InputImages(prev => { const next = [...prev]; next[slotIdx] = file; return next; });
      const reader = new FileReader();
      reader.onloadend = () => setStep2Previews(prev => { const next = [...prev]; next[slotIdx] = reader.result as string; return next; });
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleStep2RemoveSlot = (slotIdx: number) => {
    setStep2InputImages(prev => prev.filter((_, i) => i !== slotIdx));
    setStep2Previews(prev => prev.filter((_, i) => i !== slotIdx));
  };

  const handleStep2Generate = async () => {
    if (!selectedModelObj) { setStep2GenerationError('Choose an active model before generating.'); return; }
    if (step2InputImages.length === 0) return;
    setIsStep2Generating(true);
    setStep2GeneratedPreview(null);
    setStep2GenerationError(null);

    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('supportsAsync', 'true'); // opt into the async 202 + polling backend path
      if (aiModel) formData.append('model', aiModel);
      if (aspectRatio) formData.append('aspectRatio', aspectRatio);
      if (referenceImageKeys.length > 0) formData.append('referenceImageKeys', JSON.stringify(referenceImageKeys));
      step2InputImages.forEach(f => formData.append('images', f));

      // Kick off the async generation — returns a generationId immediately (202).
      const startRes = await apiUpload<GenerationStartResponse>(
        '/api/gemini/transform-with-extension',
        formData,
        GENERATION_START_TIMEOUT_MS,
      );

      if (!startRes.success || !startRes.generationId) {
        throw new Error('Failed to start image generation');
      }

      // Poll until the background generation completes (throws on failure/timeout).
      const generation = await pollGenerationUntilDone(startRes.generationId);

      const imageUrl = generation.enhancedImage?.url;
      if (!imageUrl) throw new Error('No image returned from generation');
      setStep2GeneratedPreview(imageUrl);

      const originalImageKeys = generation.originalImages?.map(img => img.key) ?? startRes.originalImageKeys;
      const enhancedImageKey = generation.enhancedImage?.key;
      if (originalImageKeys && enhancedImageKey) {
        setStep2GeneratedKeys({ originalImageKeys, enhancedImageKey });
      }
    } catch (err) {
      setStep2GenerationError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setIsStep2Generating(false);
    }
  };

  const handleAcceptStep2Generation = () => {
    if (!step2GeneratedPreview) return;
    const newPair: SamplePair = {
      key: `sample-${Date.now()}-${Math.random()}`,
      inputImages: [...step2InputImages],
      outputImageBase64: step2GeneratedPreview,
      ...(step2GeneratedKeys && {
        originalImageKeys: step2GeneratedKeys.originalImageKeys,
        enhancedImageKey: step2GeneratedKeys.enhancedImageKey,
      }),
    };
    setSamplePairs(prev => [...prev, newPair]);
    setShowStep2GenerateModal(false);
    setStep2InputImages([]);
    setStep2Previews([]);
    setStep2GeneratedPreview(null);
    setStep2GeneratedKeys(null);
  };

  const removeSample = (idx: number) => {
    if (samplePairs.length <= 1) return;
    setSamplePairs(prev => prev.filter((_, i) => i !== idx));
  };

  // === KEYWORDS ===
  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !keywords.includes(kw)) { setKeywords([...keywords, kw]); setKeywordInput(''); }
  };
  const removeKeyword = (kw: string) => setKeywords(keywords.filter(k => k !== kw));

  // === CATEGORIES ===
  const addCategory = (catId: string) => {
    if (!selectedCategories.includes(catId)) setSelectedCategories([...selectedCategories, catId]);
  };
  const removeCategory = (catId: string) => setSelectedCategories(selectedCategories.filter(c => c !== catId));
  const handleCatDragStart = (idx: number) => setDraggedCatIdx(idx);
  const handleCatDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedCatIdx === null || draggedCatIdx === idx) return;
    const newCats = [...selectedCategories];
    const dragged = newCats[draggedCatIdx];
    newCats.splice(draggedCatIdx, 1);
    newCats.splice(idx, 0, dragged);
    setSelectedCategories(newCats);
    setDraggedCatIdx(idx);
  };
  const handleCatDragEnd = () => setDraggedCatIdx(null);
  const getCategoryLabel = (catId: string) => {
    const cat = allCategories.find(c => c.category_id === catId);
    return cat ? cat.label.en : catId;
  };

  // === PUBLISH / SAVE ===
  const handlePublish = async (isPublished: boolean) => {
    // Publishing requires all critical fields (name, categories, description, a sample). The
    // Publish button is dimmed while incomplete but stays clickable, so this reports exactly
    // what's missing. Draft-saving stays lenient — it only needs a name.
    if (isPublished) {
      if (!hasSample) { setSaveError('At least one sample image is required'); return; }
      if (missingRequirements.length > 0) {
        setSaveError(`Please fill in the following before publishing:\n${missingRequirements.map(f => `• ${f}`).join('\n')}`);
        return;
      }
    } else if (!name.trim()) {
      setSaveError('Extension name is required');
      return;
    }
    setIsSaving(true);
    setSaveError(null);

    try {
      // Step 1: Create extension — always as a draft. Samples can only be uploaded once
      // the extension exists, so publishing here would leave it live with no sample if a
      // later step fails; Step 3 publishes it together with its samples.
      const userInfo = getUserInfo() || (getAccessToken() ? decodeToken(getAccessToken()!) : null);
      const creatorId = userInfo?.sub || 'admin';

      const createRes = await apiRequest<{ success: boolean; _id: string }>('/api/admin/extensions/create', {
        method: 'POST',
        body: JSON.stringify({
          Name: name.trim(),
          prompt: prompt.trim(),
          Creator: creatorId,
          CreditCost: parseInt(selectedModelCredit, 10) || 0,
          requiredPhotoCount: isDynamicInput ? 10 : inputImageCount,
          isDynamicInput,
          model: aiModel || undefined,
          categories: selectedCategories,
          aspectRatio,
          description: description.trim() || undefined,
          imageRequirements: imageRequirements.trim() || undefined,
          keywords: keywords.length > 0 ? keywords : undefined,
          isPublished: false,
        }),
      });

      if (!createRes.success || !createRes._id) {
        throw new Error('Failed to create extension');
      }

      const extensionId = createRes._id;

      // Step 2: Upload sample images and update extension
      const uploadedSamplePairs: { inputImages: string[]; outputImage: string }[] = [];

      for (let i = 0; i < samplePairs.length; i++) {
        const pair = samplePairs[i];

        // If we have S3 keys from the transform response, reuse them
        if (pair.originalImageKeys && pair.enhancedImageKey) {
          uploadedSamplePairs.push({
            inputImages: pair.originalImageKeys,
            outputImage: pair.enhancedImageKey,
          });
          continue;
        }

        // Otherwise upload manually
        const inputS3Keys: string[] = [];
        for (let j = 0; j < pair.inputImages.length; j++) {
          const formData = new FormData();
          formData.append('extensionId', extensionId);
          formData.append('sampleIndex', i.toString());
          formData.append('imageType', 'input');
          formData.append('imageIndex', j.toString());
          formData.append('image', pair.inputImages[j]);
          const uploadRes = await apiUpload<{ success: boolean; s3Key: string }>('/api/admin/extensions/upload-sample-image', formData);
          if (uploadRes.success) inputS3Keys.push(uploadRes.s3Key);
        }

        // Upload output image (convert base64 to blob)
        const base64Data = pair.outputImageBase64.split(',')[1];
        const mimeMatch = pair.outputImageBase64.match(/data:([^;]+);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        const byteChars = atob(base64Data);
        const byteArray = new Uint8Array(byteChars.length);
        for (let k = 0; k < byteChars.length; k++) byteArray[k] = byteChars.charCodeAt(k);
        const blob = new Blob([byteArray], { type: mime });
        const outputFile = new File([blob], `output_${i}.jpg`, { type: mime });

        const outputFormData = new FormData();
        outputFormData.append('extensionId', extensionId);
        outputFormData.append('sampleIndex', i.toString());
        outputFormData.append('imageType', 'output');
        outputFormData.append('imageIndex', '0');
        outputFormData.append('image', outputFile);
        const outputRes = await apiUpload<{ success: boolean; s3Key: string }>('/api/admin/extensions/upload-sample-image', outputFormData);
        // A sample without its output is dropped by the backend, so a failed upload would
        // otherwise publish the lens a sample short (or with none) without saying so.
        if (!outputRes.success || !outputRes.s3Key) {
          throw new Error(`Failed to upload sample image ${i + 1}. The lens was saved as a draft.`);
        }

        uploadedSamplePairs.push({
          inputImages: inputS3Keys,
          outputImage: outputRes.s3Key,
        });
      }

      // Step 3: Update extension with sample images and details
      await apiRequest(`/api/admin/extensions/${extensionId}/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          Name: name.trim(),
          prompt: prompt.trim(),
          aiModel: aiModel || undefined,
          categories: selectedCategories,
          aspectRatio,
          description: description.trim() || undefined,
          imageRequirements: imageRequirements.trim() || undefined,
          keywords: keywords.length > 0 ? keywords : undefined,
          sampleImages: uploadedSamplePairs,
          ...(referenceImageKeys.length > 0 ? { ReferenceImages: referenceImageKeys } : {}),
          isPublished,
        }),
      });

      doClose();
      onCreated();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save lens');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // === RENDER: GENERATION MODAL (shared overlay) ===
  const renderGenerationModal = (
    isVisible: boolean,
    generating: boolean,
    preview: string | null,
    error: string | null,
    onAccept: () => void,
    onRetry: () => void,
    onCancel: () => void,
    onGenerate?: () => void,
    inputPreviews?: string[],
  ) => {
    if (!isVisible) return null;
    return (
      <div className="gen-modal-overlay" onClick={e => e.stopPropagation()}>
        <div className="gen-modal-content" onClick={e => e.stopPropagation()}>
          {generating ? (
            <div className="gen-modal-loading">
              <FiLoader className="gen-spinner" size={48} />
              <p>Generating image...</p>
            </div>
          ) : error ? (
            <div className="gen-modal-error">
              <p style={{ color: 'var(--color-danger)' }}>{error}</p>
              <div className="gen-modal-actions">
                <button className="btn-secondary" onClick={onCancel}>Cancel</button>
                <button className="btn-primary" onClick={onRetry}>Retry</button>
              </div>
            </div>
          ) : preview ? (
            <div className="gen-modal-preview">
              <img src={preview} alt="Generated" className="gen-preview-img" />
              <div className="gen-modal-actions">
                <button className="btn-secondary" onClick={onRetry} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiRefreshCw size={14} /> Regenerate
                </button>
                <button className="btn-primary" onClick={onAccept} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiCheck size={14} /> Accept
                </button>
              </div>
            </div>
          ) : (
            // Step 2 add more: show input preview + generate button
            <div className="gen-modal-preview-inputs">
              {inputPreviews && inputPreviews.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'center' }}>
                  {inputPreviews.map((p, i) => (
                    <img key={i} src={p} alt={`Input ${i + 1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                  ))}
                </div>
              )}
              <div className="gen-modal-actions">
                <button className="btn-secondary" onClick={onCancel}>Cancel</button>
                <button className="btn-primary" onClick={onGenerate}>Generate</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {step === 2 && (
              <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#495057', display: 'flex' }}>
                <FiArrowLeft size={20} />
              </button>
            )}
            <h2>{step === 1 ? 'Create Extension' : 'Extension Details'}</h2>
          </div>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="modal-form">
          {step === 1 ? (
            <>
              {/* Prompt */}
              <div className="form-group">
                <label>Prompt *</label>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Enter the AI prompt for this lens..."
                  rows={5}
                />
              </div>

              {/* Input Images (combined row). Every photo starts as an input ("Image N");
                  click a thumbnail to toggle it to a reference ("Reference N"), which is
                  highlighted and prepended to the generation instead of counting as a sample. */}
              <div className="form-group">
                <label>Input Images *</label>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {(() => {
                    let refCount = 0;
                    let inputCount = 0;
                    return rowImages.map((img, idx) => {
                      const label = img.isReference
                        ? `Reference ${++refCount}`
                        : `Image ${++inputCount}`;
                      return (
                        <div
                          key={img.id}
                          draggable
                          onDragStart={() => handleImageDragStart(idx)}
                          onDragOver={(e) => handleImageDragOver(e, idx)}
                          onDragEnd={handleImageDragEnd}
                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '80px', opacity: draggedImageIdx === idx ? 0.4 : 1 }}
                        >
                          <div
                            onClick={() => toggleReference(img.id)}
                            title={img.isReference ? 'Click to use as a normal input image · drag to reorder' : 'Click to use as a reference image · drag to reorder'}
                            style={{ position: 'relative', width: '80px', height: '80px', cursor: 'grab' }}
                          >
                            <img
                              src={img.preview}
                              alt={label}
                              draggable={false}
                              style={{
                                width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px',
                                border: img.isReference ? '2px solid #00ffff' : '2px solid transparent',
                                boxShadow: img.isReference ? '0 0 0 1px #00ffff' : 'none',
                              }}
                            />
                            <button
                              onClick={(e) => { e.stopPropagation(); removeRowImage(img.id); }}
                              title="Remove image"
                              style={{
                                position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px',
                                borderRadius: '50%', backgroundColor: '#dc3545', color: 'white', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                              }}
                            >×</button>
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 600, marginTop: '6px', color: img.isReference ? '#008b8b' : '#6c757d' }}>
                            {label}
                          </span>
                        </div>
                      );
                    });
                  })()}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed var(--color-border)',
                      backgroundColor: 'var(--color-surface-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: 'var(--color-muted)', alignSelf: 'flex-start',
                    }}
                  >
                    <FiPlus size={24} />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleAddImages} style={{ display: 'none' }} />
                </div>
                <span style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px', display: 'block' }}>
                  Add one or more photos. Click a photo to make it a <strong>reference</strong> — it's prepended to the generation to guide style/subject (refer to it in the prompt) and won't count as a sample input.
                </span>
              </div>

              {/* AI Model + Aspect Ratio */}
              <div style={{ display: 'flex', gap: '16px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>AI Model</label>
                  <select value={aiModel} onChange={e => setAiModel(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '14px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)' }}>
                    <option value="">Select model...</option>
                    {imageModels.map(m => (
                      <option key={m._id} value={m.model_id}>{m.display_name || m.model_id}</option>
                    ))}
                  </select>
                  {aiModel && <span style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', display: 'block' }}>{selectedModelCredit} credits per generation</span>}
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Aspect Ratio</label>
                  <select value={aspectRatio} onChange={e => setAspectRatio(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '14px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)' }}>
                    {aspectRatioOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Dynamic Input Toggle */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isDynamicInput}
                    onChange={e => setIsDynamicInput(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Dynamic number of input images (max 10)
                </label>
                <span style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', display: 'block' }}>
                  Allow users to choose 1 to 10 input images when using this lens
                </span>
              </div>

              {/* Generate Button */}
              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleClose}>Cancel</button>
                <button
                  className="btn-primary"
                  onClick={handleGenerate}
                  disabled={!selectedModelObj || !prompt.trim() || inputImageCount === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <FiImage size={14} /> Generate Preview ({selectedModelCredit} credits)
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Sample Images Grid */}
              <div className="form-group">
                <label>Sample Images ({samplePairs.length})</label>
                <span style={{ fontSize: '12px', color: '#6c757d', display: 'block', marginBottom: '8px' }}>First image is the cover. Click + to add more samples.</span>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {samplePairs.map((pair, idx) => (
                    <div key={pair.key} style={{ position: 'relative', width: '100px', height: '100px' }}>
                      <img src={pair.outputImageBase64} alt={`Sample ${idx + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: idx === 0 ? '2px solid #00ffff' : '1px solid #ced4da' }} />
                      {idx === 0 && <span style={{ position: 'absolute', bottom: '4px', left: '4px', fontSize: '10px', backgroundColor: '#00ffff', color: 'var(--color-input)', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>Cover</span>}
                      {samplePairs.length > 1 && (
                        <button
                          onClick={() => removeSample(idx)}
                          style={{
                            position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px',
                            borderRadius: '50%', backgroundColor: '#dc3545', color: 'white', border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                          }}
                        >×</button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={handleStep2OpenModal}
                    style={{
                      width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed #ced4da',
                      backgroundColor: '#f8f9fa', cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: '#6c757d',
                    }}
                  >
                    <FiPlus size={24} />
                  </button>
                </div>
              </div>

              {/* Name */}
              <div className="form-group">
                <label>Lens Name *</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Give your lens a name" />
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description of what this lens does" rows={3} />
              </div>

              {/* Image Requirements */}
              <div className="form-group">
                <label>Image Requirements</label>
                <textarea value={imageRequirements} onChange={e => setImageRequirements(e.target.value)} placeholder="What kind of input images work best?" rows={2} />
              </div>

              {/* Keywords */}
              <div className="form-group">
                <label>Keywords</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type="text" value={keywordInput} onChange={e => setKeywordInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }} placeholder="Type and press Enter" style={{ flex: 1 }} />
                  <button type="button" onClick={addKeyword} style={{ padding: '8px 12px', backgroundColor: '#00ffff', color: 'var(--color-input)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><FiPlus size={14} /></button>
                </div>
                {keywords.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {keywords.map(kw => (
                      <span key={kw} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: 'var(--color-surface-hover)', borderRadius: '16px', fontSize: '13px', color: 'var(--color-text)' }}>
                        {kw}
                        <button onClick={() => removeKeyword(kw)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', color: 'var(--color-muted)' }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className="ext-form-section">
                <label className="ext-form-label">Categories</label>
                <p className="ext-form-hint">Drag to reorder. Click × to remove.</p>
                <div className="ext-categories-selected">
                  {selectedCategories.map((catId, idx) => (
                    <div key={catId} className={`ext-cat-chip ${draggedCatIdx === idx ? 'dragging' : ''}`} draggable onDragStart={() => handleCatDragStart(idx)} onDragOver={(e) => handleCatDragOver(e, idx)} onDragEnd={handleCatDragEnd}>
                      <span className="ext-cat-order">{idx + 1}</span>
                      <span>{getCategoryLabel(catId)}</span>
                      <button className="ext-cat-remove" onClick={() => removeCategory(catId)}><FiX /></button>
                    </div>
                  ))}
                </div>
                <div className="ext-categories-available">
                  <span className="ext-form-sublabel">Add category:</span>
                  <div className="ext-cat-options">
                    {allCategories.filter(c => !selectedCategories.includes(c.category_id)).map(cat => (
                      <button key={cat._id} className="ext-cat-add-btn" onClick={() => addCategory(cat.category_id)}><FiPlus /> {cat.label.en}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Save bar */}
              {saveError && <div style={{ padding: '10px 14px', backgroundColor: '#fff3f3', color: '#dc3545', borderRadius: '6px', fontSize: '14px', marginBottom: '12px', whiteSpace: 'pre-line' }}>{saveError}</div>}
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => handlePublish(false)} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save as Draft'}
                </button>
                {/* Publish is dimmed while Step 2 is incomplete but stays clickable, so a click
                    reports the missing fields (mirrors the mobile create flow). Only hard-disabled
                    while saving, to block double-submit. */}
                <button
                  className="btn-primary"
                  onClick={() => handlePublish(true)}
                  disabled={isSaving}
                  style={!isStep2Valid && !isSaving ? { opacity: 0.5 } : undefined}
                  title={!isStep2Valid ? `Missing: ${missingRequirements.join(', ') || 'sample image'}` : undefined}
                >
                  {isSaving ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Step 1 Generation Modal */}
      {renderGenerationModal(
        showGenerateModal, isGenerating, generatedPreview, generationError,
        handleAcceptGeneration, handleRetryGeneration,
        () => setShowGenerateModal(false),
      )}

      {/* Step 2 Generation Modal */}
      {showStep2GenerateModal && (
        <div className="gen-modal-overlay" onClick={e => e.stopPropagation()}>
          <div className="gen-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', padding: '24px' }}>
            {isStep2Generating && !step2GeneratedPreview ? (
              <div className="gen-modal-loading">
                <FiLoader className="gen-spinner" size={48} />
                <p>Generating image...</p>
              </div>
            ) : step2GenerationError && !step2GeneratedPreview ? (
              <div className="gen-modal-error">
                <p style={{ color: 'var(--color-danger)' }}>{step2GenerationError}</p>
                <div className="gen-modal-actions">
                  <button className="btn-secondary" onClick={() => { setShowStep2GenerateModal(false); setStep2InputImages([]); setStep2Previews([]); }}>Cancel</button>
                  <button className="btn-primary" onClick={() => { setStep2GenerationError(null); handleStep2Generate(); }}>Retry</button>
                </div>
              </div>
            ) : step2GeneratedPreview ? (
              <div className="gen-modal-preview">
                <img src={step2GeneratedPreview} alt="Generated" className="gen-preview-img" />
                <div className="gen-modal-actions">
                  <button className="btn-secondary" onClick={() => { setStep2GenerationError(null); setStep2GeneratedPreview(null); handleStep2Generate(); }} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiRefreshCw size={14} /> Regenerate
                  </button>
                  <button className="btn-primary" onClick={handleAcceptStep2Generation} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiCheck size={14} /> Accept
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Add Sample Image</h3>
                <label style={{ fontSize: '13px', color: '#6c757d', display: 'block', marginBottom: '8px' }}>
                  Input Images ({step2InputImages.filter(Boolean).length}/{isDynamicInput ? '10' : (inputImageCount || 1)})
                </label>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  {isDynamicInput ? (
                    <>
                      {step2Previews.map((preview, slotIdx) => (
                        <div key={slotIdx} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img src={preview} alt={`Input ${slotIdx + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ced4da' }} />
                          <button onClick={() => handleStep2RemoveSlot(slotIdx)} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', padding: 0 }}>×</button>
                        </div>
                      ))}
                      {step2InputImages.filter(Boolean).length < 10 && (
                        <button onClick={() => handleStep2SlotSelect(step2InputImages.length)} style={{
                          width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed #ced4da',
                          backgroundColor: '#f8f9fa', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', color: '#6c757d', gap: '4px',
                        }}>
                          <FiPlus size={24} />
                          <span style={{ fontSize: '11px' }}>Add</span>
                        </button>
                      )}
                    </>
                  ) : (
                    Array.from({ length: inputImageCount || 1 }).map((_, slotIdx) => {
                    const preview = step2Previews[slotIdx];
                    return (
                      <div key={slotIdx} style={{ position: 'relative' }}>
                        {preview ? (
                          <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                            <img src={preview} alt={`Input ${slotIdx + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ced4da' }} />
                            <button onClick={() => handleStep2RemoveSlot(slotIdx)} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', padding: 0 }}>×</button>
                          </div>
                        ) : (
                          <button onClick={() => handleStep2SlotSelect(slotIdx)} style={{
                            width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed #ced4da',
                            backgroundColor: '#f8f9fa', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', color: '#6c757d', gap: '4px',
                          }}>
                            <FiPlus size={24} />
                            <span style={{ fontSize: '11px' }}>Image {slotIdx + 1}</span>
                          </button>
                        )}
                      </div>
                    );
                  })
                  )}
                </div>
                <div className="gen-modal-actions">
                  <button className="btn-secondary" onClick={() => { setShowStep2GenerateModal(false); setStep2InputImages([]); setStep2Previews([]); }}>Cancel</button>
                  <button className="btn-primary" onClick={handleStep2Generate} disabled={!selectedModelObj || step2InputImages.filter(Boolean).length === 0} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiImage size={14} /> Generate ({selectedModelCredit} credits)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
